import { toPairs } from 'lodash';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { AuthenticationContext } from '../../shared/contexts/authentication.context';
import { CurrentClusterContext } from '../../shared/contexts/current-cluster.context';
import { ModalViewProperties } from '../modal-view-properties';
import { StepsToReproduce } from '../steps-to-reproduce';
import { lastValueFrom, Subscription, switchMap, tap } from 'rxjs';
import {
  ClusterResult,
  CurrentUser,
  FrontendError,
  GlobalProperties,
  Role,
} from '../../shared/models/generated/ziroModels';
import { ButtonStyles, ZiroButtonComponent } from '../../button/button.component';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { GlobalPropertiesContext } from '../../shared/contexts/global-properties.context';
import { MicrosoftTenantsStateContext } from '../../shared/contexts/microsoft-tenants-state.context';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../../shared/shared.module';
import { PipesModule } from '../../shared/pipes/pipes.module';
import { filter } from 'rxjs/operators';

enum ModalState {
  STACKTRACE,
  SEND_REPORT,
  REPORT_SENT,
}

@Component({
  selector: 'ziro-oops-modal',
  templateUrl: './oops-modal.component.html',
  styleUrls: ['./oops-modal.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbModule,
    TranslateModule,
    ZiroButtonComponent,
    SharedModule,
    PipesModule,
  ],
})
export class OopsModalComponent implements OnInit, OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private http = inject(HttpClient);
  private router = inject(Router);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private authenticationContext = inject(AuthenticationContext);
  private currentClusterContext = inject(CurrentClusterContext);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);

  ModalState = ModalState;
  state = ModalState.STACKTRACE;
  modalViewProperties: ModalViewProperties;
  stepsToReproduce: StepsToReproduce;
  oopsDialogForm: any;
  currentUser: CurrentUser;
  canSendReport = false;

  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;

  mailToInfo = {
    email: 'softwaresupport@goziro.com',
    subject: encodeURIComponent('Ziro Error Report'),
  };

  private _subs = new Subscription();
  private _currentCluster: ClusterResult;
  private _globalProperties: GlobalProperties;
  private _currentTenant = '';

  ngOnInit(): void {
    const authSub = this.authenticationContext.state$.subscribe((user: CurrentUser) => {
      this.currentUser = user;
      this.canSendReport = this.authenticationContext.userIsAtLeast(user, Role.S8_ADMIN);
    });
    this._subs.add(authSub);
    const currentContextSub = this.currentClusterContext.state$.subscribe((data: ClusterResult) => {
      this._currentCluster = data;
    });
    this._subs.add(currentContextSub);

    const globalPropertySub = this.globalPropertiesContext.state$
      .pipe(
        tap((globalProperties) => {
          this._globalProperties = globalProperties;
        }),
        filter((globalProperties) => globalProperties.hostedEnabled),
        switchMap(() => this._microsoftTenantsStateContext.cachedTenantState$),
        tap((cachedTenant) => {
          this._currentTenant = cachedTenant?.tenantName || '';
        }),
      )
      .subscribe();

    this._subs.add(globalPropertySub);

    this.initializeStacktrace();
    this.sendErrorToServer().then(
      (data) => {
        console.warn('Error data sent to server:', data);
      },
      (error) => {
        console.error('Error sending client log:', error);
      },
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  // TODO: reload if on 'home' state
  onHomeClicked(): void {
    this.goToUserHome(this.currentUser);
  }

  onClose(): void {
    this.activeModal.close();
  }

  sendSupportEmail(): void {
    if (!this.modalViewProperties.stepsToReproduce || this.modalViewProperties.stepsToReproduce.length < 5) {
      return;
    }

    const developerProperties = this._buildDeveloperProperties();
    const propertiesToAppend = toPairs(developerProperties)
      .map((entry) => `${entry[0]} = ${entry[1]}`)
      .join('\n');

    const { email, subject } = this.mailToInfo;

    this.modalViewProperties.stepsToReproduce += '\n\n' + propertiesToAppend;

    window.open(
      `mailto:${email}?subject=${subject}&body=${encodeURIComponent(this.modalViewProperties.stepsToReproduce)}`,
    );

    this.state = ModalState.REPORT_SENT;
  }

  initializeStacktrace(): void {
    const stacktrace = this.modalViewProperties['stacktrace'];
    if (stacktrace && typeof stacktrace !== 'string') {
      try {
        this.modalViewProperties['stacktrace'] = JSON.stringify(stacktrace, null, 2);
      } catch (e) {
        // Don't rethrow the exception otherwise it'll be error modal inception
        console.error('Tried to pretty-print stacktrace, but failed: ' + e);
      }
    }

    const stacktraceHasNewLines = stacktrace && typeof stacktrace === 'string' && stacktrace.split('\n').length > 1;
    if (stacktraceHasNewLines && !this.modalViewProperties['errorSummary']) {
      // Show the first line of the stack trace as the error summary if none was provided
      this.modalViewProperties['errorSummary'] = stacktrace.split('\n')[0];
    }

    this.modalViewProperties.expanded = this.modalViewProperties.stacktrace && !this.modalViewProperties.errorSummary;
  }

  sendErrorToServer(): Promise<any> {
    const obs = this.http.post('/services/logs/client-error', this._buildDeveloperProperties());
    return (window as any).environment.production ? lastValueFrom(obs) : Promise.reject('skipping (in dev mode)');
  }

  private _buildDeveloperProperties(): FrontendError {
    const developerProperties = {
      currentUrl: window.location.href,
      userAgent: navigator.userAgent,
    } as FrontendError;

    if (this._globalProperties) {
      developerProperties.buildVersion = this._globalProperties.version;
    }

    const isHosted = this._globalProperties?.hostedEnabled;
    developerProperties.clusterOrTenant = isHosted ? this._currentTenant : this._currentCluster?.name || '';

    if (this.currentUser?.role) {
      developerProperties.currentUser = this.currentUser.userId;
      developerProperties.currentUserRole = this.currentUser.role;
    }

    if (this.modalViewProperties['errorSummary']) {
      developerProperties.errorSummary = this.modalViewProperties['errorSummary'];
    }

    return developerProperties;
  }

  private goToUserHome(currentUser: CurrentUser): void {
    this.activeModal.close();

    if (this.authenticationContext.userIsAtLeast(currentUser, Role.S8_ADMIN)) {
      this.router.navigateByUrl('/home');
    } else if (currentUser.role === Role.S8_SELF_SERVE_USER) {
      this.router.navigateByUrl('/self-serve');
    } else {
      this.router.navigateByUrl('/');
    }
  }
}
